/**
 * Edit-report panel for VSC reports.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { VscReportDetailsPanel } from '@/components/vsc-reports/renderVscReportDetails';
import { getVscReport } from '@/services/vsc-reports.service';
import { VSC_REPORT_FORM_QUERY_KEYS } from './vsc-report-configs';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';

/** Props for {@link EditVscReportView}. Wraps {@link VscReportDetailsPanel} (footer hidden). */
interface EditVscReportViewProps {
  vscReportId: string;
  onClose: () => void;
}

/** Edit-report panel for VSC reports. */
export const EditVscReportView: React.FC<Readonly<EditVscReportViewProps>> = ({
  vscReportId,
  onClose,
}) => {
  const {
    data: vscReport,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: VSC_REPORT_FORM_QUERY_KEYS.one(vscReportId),
    queryFn: () => getVscReport(vscReportId),
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'vscReportNotFound',
    onClose,
    entityId: vscReportId,
  });

  let content: React.ReactNode = null;
  if (vscReport) {
    content = (
      <VscReportDetailsPanel
        vscReport={vscReport}
        presentationMode="edit"
        hideDetailHeader
        onActionSuccess={onClose}
      />
    );
  }

  return (
    <CollapseReplaceForm
      formType="edit"
      title="Modifier le rapport VSC"
      subtitle="Modifiez les informations du rapport VSC"
      onClose={onClose}
      onSubmit={() => {}}
      submitLabel="Enregistrer"
      isSubmitting={false}
      isValid
      isLoading={isLoading}
      showFooter={false}
    >
      {content}
    </CollapseReplaceForm>
  );
};
